import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { getCampaignConfiguration } from 'apis/restaurant-api/merchant-admin/campaigns/api';
import { CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';

import { CampaignsQueryKeys } from '../campaignsQueryKeys';

// we don't have separate configuration for EoD, because we create them as item discount campaigns:
// https://woltwide.atlassian.net/wiki/spaces/MER/pages/3327492880/One-Pager+End+of+Day+campaigns
const DEFAULT_CAMPAIGN_TYPES: CampaignType[] = [CampaignType.ADS, CampaignType.END_OF_DAY];

type UseCampaignConfigurationProps = {
  merchantId: string;
  venueIds: string[];
  campaignTypesPermittedToEdit: CampaignType[];
  enabled?: boolean;
};

export const useCampaignConfiguration = ({
  merchantId,
  venueIds,
  campaignTypesPermittedToEdit,
  enabled = true,
}: UseCampaignConfigurationProps) => {
  const configurationQuery = useQuery(
    CampaignsQueryKeys.GET_CONFIG(merchantId, venueIds),
    () => getCampaignConfiguration(merchantId, venueIds),
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled,
    },
  );

  const campaignTypesAvailableToEdit = useMemo(() => {
    if (!configurationQuery.data) {
      return [];
    }

    const configuredCampaignTypes = [...configurationQuery.data.campaign_types, ...DEFAULT_CAMPAIGN_TYPES];

    return configuredCampaignTypes?.filter(type => campaignTypesPermittedToEdit.includes(type));
  }, [campaignTypesPermittedToEdit, configurationQuery.data]);

  return {
    configurationQuery,
    campaignTypesAvailableToEdit,
    campaignsNumberLimit: configurationQuery.data?.promotion_per_venue_limit,
  };
};
